import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { ApproachVersion } from 'types/approach';
import './VersionSelectorAtom.scss';

export interface VersionSelectorAtomProps {
  versions: ApproachVersion[];
  currentVersion?: number;
  onChange: (version: number) => void;
  disabled?: boolean;
  className?: string;
}

interface VersionOption {
  label: string;
  value: number;
}

const VersionSelectorAtom: React.FC<VersionSelectorAtomProps> = ({
  versions,
  currentVersion,
  onChange,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();

  const latestPublished = useMemo(() => {
    const published = versions
      .filter((v) => !v.is_draft)
      .sort((a, b) => b.version - a.version);
    return published.length > 0 ? published[0].version : null;
  }, [versions]);

  const options: VersionOption[] = useMemo(() => {
    return [...versions]
      .sort((a, b) => b.version - a.version)
      .map((v) => {
        const versionLabel = t('marketplace.approachDetail.versions.versionLabel', {
          version: v.version,
        });
        const suffixes: string[] = [];
        if (v.is_draft) {
          suffixes.push(t('marketplace.approachDetail.versions.draftLabel'));
        }
        if (!v.is_draft && v.version === latestPublished) {
          suffixes.push(t('marketplace.approachDetail.versions.latestLabel'));
        }
        const suffix = suffixes.length > 0 ? ` (${suffixes.join(', ')})` : '';
        return {
          label: `${versionLabel}${suffix}`,
          value: v.version,
        };
      });
  }, [versions, latestPublished, t]);

  const handleChange = (e: DropdownChangeEvent) => {
    if (e.value != null) {
      onChange(e.value as number);
    }
  };

  const classes = ['marketplace-version-selector', className].filter(Boolean).join(' ');

  return (
    <Dropdown
      className={classes}
      value={currentVersion}
      options={options}
      onChange={handleChange}
      optionLabel="label"
      optionValue="value"
      placeholder={t('marketplace.approachDetail.versions.selectVersion')}
      disabled={disabled}
    />
  );
};

export default VersionSelectorAtom;
